import COLORS from '@/constants/colors';
import { useGoBack } from '@/hooks/useGoBack';
import { useIsMobile } from '@/utils/isMobile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { ButtonUiState } from '../Button/ButtonUi';

export type HeaderSection = {
  title: string;
  subtitle?: string;
  showReturn?: boolean;
  primaryButton?: ButtonUiState;
  onPrimaryButtonClicked?: () => void;
  secondaryButton?: ButtonUiState;
  onSecondaryButtonClicked?: () => void;
  sx?: SxProps;
};

export function HeaderSection(props: HeaderSection) {
  const goBackHook = useGoBack();
  const handleReturnClick = () => goBackHook.goBack();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const isMobile = useIsMobile();

  return (
    <Stack spacing={1} px={'0px'} sx={{ ...props.sx }} display={'flex'}>
      {props.showReturn && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            width: 'fit-content',
          }}
          onClick={handleReturnClick}
        >
          <ArrowBackIcon sx={{ color: COLORS.green }} />
          <Typography
            sx={{
              fontSize: { xs: '12px', md: '14px' },
              fontWeight: 500,
              color: COLORS.olive,
            }}
          >
            Regresar
          </Typography>
        </Box>
      )}
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Typography
          sx={{
            color: COLORS.gray_900,
            fontSize: { xs: '18px', md: '30px' },
            fontFamily: 'Inter',
          }}
          variant="h6"
        >
          {props.title}
        </Typography>

        <Stack direction="row" spacing={2}>
          {props.primaryButton && props.secondaryButton ? (
            isMobile ? (
              <>
                <IconButton onClick={handleMenuOpen}>
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={isMenuOpen}
                  onClose={handleMenuClose}
                >
                  <MenuItem
                    sx={{ gap: 1 }}
                    onClick={() => {
                      handleMenuClose();
                      props.onPrimaryButtonClicked?.();
                    }}
                    disabled={
                      props.primaryButton.disabled ||
                      props.primaryButton.loading
                    }
                  >
                    {props.primaryButton.loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <>
                        {props.primaryButton.leftIcon &&
                          props.primaryButton.leftIcon}
                        {props.primaryButton.label}
                      </>
                    )}
                  </MenuItem>
                  <MenuItem
                    sx={{ gap: 1 }}
                    onClick={() => {
                      handleMenuClose();
                      props.onSecondaryButtonClicked?.();
                    }}
                    disabled={
                      props.secondaryButton.disabled ||
                      props.secondaryButton.loading
                    }
                  >
                    {props.secondaryButton.loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <>
                        {props.secondaryButton.leftIcon &&
                          props.secondaryButton.leftIcon}
                        {props.secondaryButton.label}
                      </>
                    )}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                {props.primaryButton && (
                  <Button
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: COLORS.olive,
                      gap: 1,
                    }}
                    variant={props.primaryButton.variant}
                    onClick={props.onPrimaryButtonClicked}
                    disabled={
                      props.primaryButton.disabled ||
                      props.primaryButton.loading
                    }
                  >
                    {props.primaryButton.loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <>
                        {props.primaryButton.leftIcon &&
                          props.primaryButton.leftIcon}
                        {props.primaryButton.label}
                      </>
                    )}
                  </Button>
                )}
                {props.secondaryButton && (
                  <Button
                    variant={props.secondaryButton.variant}
                    onClick={props.onSecondaryButtonClicked}
                    disabled={
                      props.secondaryButton.disabled ||
                      props.secondaryButton.loading
                    }
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: COLORS.olive,
                      gap: 1,
                    }}
                  >
                    {props.secondaryButton.loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <>
                        {props.secondaryButton.leftIcon &&
                          props.secondaryButton.leftIcon}
                        {props.secondaryButton.label}
                      </>
                    )}
                  </Button>
                )}
              </>
            )
          ) : (
            <>
              {props.primaryButton && (
                <Button
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: COLORS.olive,
                    gap: 1,
                  }}
                  variant={props.primaryButton.variant}
                  onClick={props.onPrimaryButtonClicked}
                  disabled={
                    props.primaryButton.disabled || props.primaryButton.loading
                  }
                >
                  {props.primaryButton.loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <>
                      {props.primaryButton.leftIcon &&
                        props.primaryButton.leftIcon}
                      {props.primaryButton.label}
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </Stack>
      </Box>

      {props.subtitle && (
        <Typography
          sx={{
            color: COLORS.gray_600,
            fontSize: { xs: '14px', md: '16px' },
            fontWeight: '400',
          }}
          variant="subtitle2"
        >
          {props.subtitle}
        </Typography>
      )}
    </Stack>
  );
}
